import React, { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import {
  DeveloperBoardOutlined,
  EnergySavingsLeafOutlined,
  GroupsOutlined,
  LightbulbOutlined,
  PrecisionManufacturingOutlined,
  SupportAgentOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";

import LinkButton from "../components/ui/LinkButton";
import ContentSection from "../components/ContentSection";
import CompactCard from "../components/CompactCard";
import IconedItemsList from "../components/ui/IconedItemsList";

import BlueMountains from "../assets/images/BlueMountains.webp";
import SunnyMountains from "../assets/images/SunnyMountains.jpg";
import BackgroundImageBox from "../components/ui/BackgroundImageBox";
import HeroSectionV2 from "../components/HeroSectionV2";

function AboutPage() {
  return (
    <Fragment>
      <HeroSectionV2 heading="About us" subheading="Our mission and what we do">
        <Box display="flex" gap={6} flexDirection={{ xs: "column", lg: "row" }}>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography color="#001b35" variant="h6">
              Flamestone is a part of the Flame Group, a multinational company
              with over a decade of experience in the IT market and consumer
              electronics.
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Headquartered in the Czech Republic, Flame Group
              operates across Europe, Asia, and the Middle East, delivering
              cutting-edge hardware and software solutions tailored to the
              unique needs of various industries. Our expertise spans from
              designing and manufacturing advanced technology to providing
              logistics, customer support, and consulting services.
            </Typography>
            <Box
              sx={{
                display: "flex",
                marginTop: 4,
                gap: 1,
              }}
            >
              <LinkButton to="/biogas" size="large">
                Read More
              </LinkButton>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              backgroundImage: `url(${BlueMountains})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 8,
              minHeight: "200px",
            }}
          />
        </Box>
      </HeroSectionV2>
      <Box
        sx={{
          // backgroundImage: `url(${Svg})`,
          backgroundSize: { xs: "100%", lg: "60%" },
          backgroundPosition: {
            xs: "200px 40%",
            lg: "bottom -400px left -200px",
          },
          backgroundRepeat: "no-repeat",
        }}
      >
        <ContentSection>
          <Box
            sx={{
              flex: 1,
              flexShrink: 0,
              textAlign: "center",
              margin: "0 auto",
            }}
          >
            <Typography color="#001b35" variant="h3" marginTop={2}>
              Our Mission
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              At Flamestone, we are committed to empowering a sustainable future
              by investing in green technologies and innovative solutions.
              Leveraging our heritage of technological excellence, we are
              expanding into renewable energy sectors, focusing on biogas
              plants, biomethane production, BioLNG, and BioCNG facilities. Our
              goal is to drive meaningful change by supporting efficient,
              scalable, and environmentally friendly energy systems.
            </Typography>
            <Box
              sx={{
                flexDirection: { xs: "column", lg: "row" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 3,
                gap: 1,
              }}
            >
              <LinkButton to="/biogas" size="large">
                Explore Biogas Solutions
              </LinkButton>
              <LinkButton to="/energy" size="large">
                Explore Energy tech
              </LinkButton>
            </Box>
          </Box>
        </ContentSection>
      </Box>
      <BackgroundImageBox image={BlueMountains}>
        <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography color="#001b35" variant="h2" marginBlockStart={2}>
            What We Do
          </Typography>
          <Typography
            color="#001b35"
            variant="body1"
            marginTop={2}
          >
            Building on the Flame Group’s legacy of innovation and partnerships
            with global leaders like Foxconn, Intel, Pegatron, and TUV-SUD,
            Flamestone specialises in the following areas:
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: { xs: "column", lg: "row" },
            flexShrink: 0,
            gap: 4,
            marginTop: 4,
          }}
        >
          {[
            {
              heading: "Advanced Hardware and Software Development",
              description:
                "Customised server solutions for data centres, AI platforms, and IoT-enabled systems.",
              icon: DeveloperBoardOutlined,
            },
            {
              heading: "Renewable Energy Investments",
              description:
                "Supporting the development of biogas-to-biomethane technologies, including liquefied and compressed biofuels.",
              icon: EnergySavingsLeafOutlined,
            },
            {
              heading: "R&D and Manufacturing",
              description:
                "From initial prototyping to full-scale production, we ensure quality and scalability by collaborating with manufacturers in Europe and Asia.",
              icon: PrecisionManufacturingOutlined,
            },
            {
              heading: "Customer-Centric Services",
              description:
                "Tailored solutions for clients, including hardware customisation, software localisation, and post-service support.",
              icon: SupportAgentOutlined,
            },
          ].map(({ heading, description, icon }) => (
            <Box
              key={heading}
              boxSizing="border-box"
              width={{ sx: "100%", lg: "25%" }}
            >
              <CompactCard
                key={heading}
                icon={icon}
                title={heading}
                description={description}
                sx={{ textAlign: "left" }}
              />
            </Box>
          ))}
        </Box>
      </BackgroundImageBox>
      <Box
        backgroundColor="#E8F5E9"
        sx={{ marginBottom: { xs: "200px", lg: "150px" } }}
      >
        <ContentSection>
          <Box
            sx={{
              padding: { xs: 0, lg: 6 },
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <Typography color="#001b35" variant="h6">
                Our Values
              </Typography>
              <IconedItemsList
                marginBlockStart={4}
                textAlign="left"
                items={[
                  {
                    heading: "Innovation",
                    description:
                      "Staying at the forefront of technology to deliver impactful solutions.",
                    icon: LightbulbOutlined,
                  },
                  {
                    heading: "Sustainability",
                    description:
                      "Focusing on reducing carbon footprints and promoting energy efficiency.",
                    icon: EnergySavingsLeafOutlined,
                  },
                  {
                    heading: "Quality",
                    description:
                      "Upholding high standards in every product and service we offer.",
                    icon: VerifiedOutlined,
                  },
                  {
                    heading: "Collaboration",
                    description:
                      "Building strong relationships with partners, customers, and stakeholders worldwide.",
                    icon: GroupsOutlined,
                  },
                ]}
              />
            </Box>
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <img
                alt=""
                src={SunnyMountains}
                style={{
                  borderRadius: 32,
                  marginBottom: "-200px",
                  height: "550px",
                  width: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Box>
        </ContentSection>
      </Box>
    </Fragment>
  );
}

export default AboutPage;
